<script lang="ts" setup>
import { capitalize } from '@/util/string.util';
import { ref, watch, PropType, computed } from 'vue';
import { MdEditor, ToolbarNames } from 'md-editor-v3';
import 'md-editor-v3/lib/style.css';
import MediaToolbar from '@/components/common/MediaToolbar.vue';
import LinkToolbar from '@/components/common/LinkToolbar.vue';
import { Media } from '@/models/media.model';

type ValidationResult = string | boolean;
type ValidationRule$1 =
  | ValidationResult
  | PromiseLike<ValidationResult>
  | ((value: string) => ValidationResult)
  | ((value: string) => PromiseLike<ValidationResult>);

const props = defineProps({
  label: { type: String, default: () => 'Label' },
  height: { type: Number, default: () => 400 },
  hint: { type: String, default: () => '' },
  rules: {
    type: Array as PropType<ValidationRule$1[]>,
    default: () => [],
  },
  disabled: { type: Boolean, default: () => false },
  modelValue: { type: String, required: true, default: () => '' },
  media: { type: Array as PropType<Media[]>, default: () => [] },
});

const text = ref<string>(props.modelValue || '');

const toolbars: ToolbarNames[] = [
  'bold',
  'underline',
  'italic',
  'strikeThrough',
  '-',
  'unorderedList',
  'orderedList',
  '-',
  'title',
  'image',
  '-',
  0, // first custom toolbar
  1,
];

const emit = defineEmits(['update:modelValue', 'change']);

const errors = computed<boolean | string>(() => {
  const errs = props.rules?.map((r) => r(text.value));

  if (errs.some((v) => typeof v === 'string')) {
    return errs.map((v) => (typeof v === 'string' ? v : '')).join('');
  }
  return true;
});

function update(v: string) {
  text.value = v;
  emit('update:modelValue', text.value);
  emit('change');
  showHint.value = true;
}

watch(
  () => props.modelValue,
  () => {
    text.value = props.modelValue || '';
  },
  { deep: true },
);

const showHint = ref<boolean>(false);
</script>
<template>
  <div
    class="rich-text-field"
    :class="{
      'rich-text-field-disabled': disabled,
      'rich-text-field-invalid': !disabled && errors !== true,
    }"
  >
    <label>{{ capitalize(label) }} </label>
    <MdEditor
      v-model="text"
      language="en-US"
      :disabled="disabled"
      :toolbars="disabled ? [] : toolbars"
      :toolbarsExclude="['image']"
      :style="{ height: `${height}px` }"
      @change="(v) => update(v)"
    >
      <template #defToolbars>
        <MediaToolbar :media="media" />
        <LinkToolbar />
      </template>
    </MdEditor>
    <!-- <p v-if="!isValid">errorMessages: {{ errorMessages }}</p> -->
  </div>
  <div class="messages">
    <v-messages
      :active="showHint"
      transition="slide-y-transition"
      :messages="[hint]"
    />
    <v-messages
      color="#a70000"
      :active="!disabled && errors !== true"
      transition="slide-y-transition"
      :messages="[errors as string]"
    />
  </div>
</template>
<style>
.md-editor,
.cm-editor {
  background-color: #f6f6f6;
}

.rich-text-field {
  background-color: #f6f6f6;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom-left-radius: 0px;
  border-bottom-right-radius: 0px;
  border-bottom: solid 1px #a5a5a5;
  box-sizing: border-box;
  padding-top: 6px;
}

.rich-text-field-invalid {
  border-bottom: solid 1px #a70000;
}
.rich-text-field-disabled {
  border-bottom: dashed 1px #cbcbcb;
}
.rich-text-field-disabled,
.rich-text-field-disabled .md-editor,
.rich-text-field-disabled .cm-editor {
  background-color: #fcfcfc;
}

.rich-text-field label {
  padding-left: 16px;
  font-size: 13px;
  font-family: inherit;
  color: #787878;
}
.rich-text-field-invalid label {
  color: #a70000;
}
.rich-text-field-disabled label {
  color: #cbcbcb;
}

.v-md-editor {
  border-radius: 0;
  background-color: transparent !important;
  box-shadow: none;
}

.v-md-textarea-editor textarea {
  font-family: Roboto, sans-serif !important;
}
.github-markdown-body {
  color: gray;
  padding: 1em;
  font-size: 14px;
  font-family: Roboto, sans-serif !important;
}
.github-markdown-body p,
.github-markdown-body ul,
.github-markdown-body ol {
  margin-bottom: 1em;
}

.github-markdown-body ul,
.github-markdown-body ol {
  padding-left: 2em;
}

.messages {
  position: relative;
  height: auto;
  padding-top: 0.2em;
  overflow: hidden;
  margin-bottom: 1em;
}
</style>
